import { useState, type ChangeEvent, type ReactNode } from 'react';
import { ExternalLink, MoreHorizontal, Radio, AlertTriangle } from 'lucide-react';
import { useIntegrations, useDisconnectIntegration } from '@/hooks';
import { integrationService } from '@/services';
import type { MintsoftClientList, MintsoftCredentials } from '@/services/integration.service';
import { toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { confirmDisconnect } from './disconnect-confirm.store';
import type { Integration } from '../types';

const MINTSOFT_STATIC_KEY_GUIDE_URL = 'https://intercom.help/mintsoft/en/articles/11600570-api-set-a-static-api-key';
const MINTSOFT_API_GUIDE_URL = 'https://help-mintsoft.theaccessgroup.com/en/articles/11784313-supporting-you-with-apis';
const MINTSOFT_SWAGGER_URL = 'https://api.mintsoft.co.uk/swagger/ui/index#/';

/** What the connected Mintsoft user must be allowed to do, and the Mintsoft role that grants it. */
const MINTSOFT_ROLES = [
	{ action: 'Read order status, tracking, comments, splits and returns', role: 'AdminViewOrders or ClientViewOrders', required: true },
	{ action: 'Add order comments for the warehouse', role: 'AdminEditOrder or ClientEditOrder', required: false },
	{ action: 'Cancel orders before despatch', role: 'AdminCancelOrder or ClientCancelOrder', required: false },
	{ action: 'Load the client list', role: 'Warehouse (admin) user, not a client user', required: false }
] as const;

type AuthType = MintsoftCredentials['auth_type'];

const MAX_FIELD_LENGTH = 500;

const errorMessage = (err: unknown, fallback: string) =>
	(err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? fallback;

const GuideStep = ({ step, children }: { step: number; children: ReactNode }) => (
	<li className='flex gap-2'>
		<span className='flex size-4 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-medium'>{step}</span>
		<div className='flex flex-1 flex-col gap-1 text-xs'>{children}</div>
	</li>
);

const Strong = ({ children }: { children: ReactNode }) => <strong className='font-medium'>{children}</strong>;

/** Who can set Mintsoft up for Kim, the roles it needs, and the steps for the chosen sign-in method. */
const SetupGuide = ({ authType }: { authType: AuthType }) => (
	<div className='flex flex-col gap-3 rounded-lg border p-3'>
		<div className='flex items-center justify-between'>
			<p className='text-xs font-medium'>Before you connect</p>
			<a href={MINTSOFT_STATIC_KEY_GUIDE_URL} target='_blank' rel='noreferrer' className='inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground'>
				Mintsoft guide <ExternalLink className='size-3' />
			</a>
		</div>

		<div className='flex flex-col gap-1 text-xs'>
			<p className='font-medium'>Who can do this</p>
			<p className='text-muted-foreground'>
				Only a <Strong>3PL administrator</Strong> or <Strong>database administrator</Strong> in Mintsoft can set an API key to never expire. A
				3PL&apos;s client users generate their own key — warehouse and admin users cannot see a client user&apos;s key, so the client user has
				to create it and share it. We recommend a dedicated Mintsoft user for Kim.
			</p>
		</div>

		<div className='flex flex-col gap-1.5 text-xs'>
			<p className='font-medium'>Access the Mintsoft user needs</p>
			<div className='flex flex-col divide-y rounded-md border'>
				{MINTSOFT_ROLES.map(({ action, role, required }) => (
					<div key={action} className='flex flex-col gap-0.5 px-2 py-1.5'>
						<span className='flex items-center justify-between gap-2'>
							<span>{action}</span>
							<span className='shrink-0 text-[10px] text-muted-foreground'>{required ? 'Required' : 'Optional'}</span>
						</span>
						<code className='font-mono text-[11px] text-muted-foreground'>{role}</code>
					</div>
				))}
			</div>
		</div>

		{authType === 'api_key' ? (
			<ol className='flex flex-col gap-2.5' aria-label='How to get a static API key'>
				<GuideStep step={1}>
					<span>
						In Mintsoft, go to <Strong>Settings → Change Password</Strong> and set a Mintsoft password.
					</span>
					<span className='text-[11px] text-muted-foreground'>It can differ from your Access Evo password; it is the one used to generate the key.</span>
				</GuideStep>
				<GuideStep step={2}>
					<span>
						Open the{' '}
						<a href={MINTSOFT_SWAGGER_URL} target='_blank' rel='noreferrer' className='underline'>
							Mintsoft API page
						</a>
						, find <Strong>POST /api/Auth</Strong>, enter your Mintsoft username and that password, and click <Strong>Try it out!</Strong>
					</span>
					<span className='text-[11px] text-muted-foreground'>The response is your API key. On its own it expires after 24 hours.</span>
				</GuideStep>
				<GuideStep step={3}>
					<span>
						Make it permanent: <Strong>Settings → Warehouse User Accounts</Strong> (or <Strong>Client User Accounts</Strong>), search for the user,
						then <Strong>Actions → Set API Key to Never Expire</Strong>.
					</span>
					<span className='text-[11px] text-muted-foreground'>
						No API Key column? Open <Strong>Column Visibility</Strong>, tick <Strong>API Key</Strong> and click <Strong>Apply</Strong>.
					</span>
				</GuideStep>
				<GuideStep step={4}>
					<span>Copy the key and paste it above.</span>
				</GuideStep>
			</ol>
		) : (
			<ol className='flex flex-col gap-2.5' aria-label='How to sign in with a username and password'>
				<GuideStep step={1}>
					<span>Pick (or create) the Mintsoft user Kim should sign in as, and give it the access above.</span>
				</GuideStep>
				<GuideStep step={2}>
					<span>
						Signed in as that user, go to <Strong>Settings → Change Password</Strong> and set its Mintsoft password.
					</span>
					<span className='text-[11px] text-muted-foreground'>It can differ from the Access Evo password — use the Mintsoft one here.</span>
				</GuideStep>
				<GuideStep step={3}>
					<span>Enter the username and password above.</span>
					<span className='text-[11px] text-muted-foreground'>
						Kim exchanges them for a 24-hour API key and renews it automatically, so nothing needs to be set to never expire. Changing the
						password in Mintsoft disconnects Kim until you update it here.
					</span>
				</GuideStep>
			</ol>
		)}

		<a href={MINTSOFT_API_GUIDE_URL} target='_blank' rel='noreferrer' className='inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground'>
			More about Mintsoft API access <ExternalLink className='size-3' />
		</a>
	</div>
);

const text = (v: unknown) => (typeof v === 'string' ? v : '');

const describeClient = (meta: Record<string, unknown>) => {
	const name = text(meta.client_name);
	const id = text(meta.client_id);
	if (name && id) return `${name} (${id})`;
	return name || id || 'Every client the key can see';
};

export const MintsoftContent = ({ integration }: { integration: Integration }) => {
	const [isEditing, setIsEditing] = useState(false);
	const [authType, setAuthType] = useState<AuthType>('api_key');
	const [apiKey, setApiKey] = useState('');
	const [username, setUsername] = useState('');
	const [password, setPassword] = useState('');
	const [clientId, setClientId] = useState('');
	const [clientName, setClientName] = useState('');
	const [clientList, setClientList] = useState<MintsoftClientList | null>(null);
	const [isLoadingClients, setIsLoadingClients] = useState(false);
	const [isConnecting, setIsConnecting] = useState(false);
	const disconnectMutation = useDisconnectIntegration();
	const { refetch } = useIntegrations();

	const credentials: MintsoftCredentials | null =
		authType === 'api_key'
			? apiKey.trim()
				? { auth_type: 'api_key', api_key: apiKey.trim() }
				: null
			: username.trim() && password
				? { auth_type: 'credentials', username: username.trim(), password }
				: null;

	/** Start from the saved connection, so updating a key never silently drops its client scope. */
	const startEditing = () => {
		const meta = integration.authMetaData ?? {};
		setAuthType(meta.auth_type === 'credentials' ? 'credentials' : 'api_key');
		setUsername(text(meta.username));
		setClientId(text(meta.client_id));
		setClientName(text(meta.client_name));
		setIsEditing(true);
	};

	/** Secrets never outlive the form. */
	const closeForm = () => {
		setApiKey('');
		setPassword('');
		setClientList(null);
		setIsEditing(false);
	};

	const selectClient = (id: string, name = '') => {
		setClientId(id);
		setClientName(name);
	};

	/** New credentials may see a different set of clients. */
	const onCredentialChange = (set: (v: string) => void) => (e: ChangeEvent<HTMLInputElement>) => {
		set(e.target.value);
		setClientList(null);
	};

	const handleLoadClients = async () => {
		if (!credentials) return;
		setIsLoadingClients(true);
		try {
			const list = await integrationService.fetchMintsoftClients(credentials);
			setClientList(list);
			const current = list.clients.find((c) => c.id === clientId.trim());
			if (list.clients.length === 1) selectClient(list.clients[0].id, list.clients[0].name);
			else if (current) setClientName(current.name);
			// A typed id this account does not have would be sent while the picker shows "No client filter".
			else if (list.clients.length > 0) selectClient('');
		} catch (err) {
			toast.error(errorMessage(err, 'Could not load Mintsoft clients. Check the details, or enter the client ID by hand.'));
		} finally {
			setIsLoadingClients(false);
		}
	};

	const handleConnect = async () => {
		if (!credentials) return;
		setIsConnecting(true);
		try {
			const id = clientId.trim();
			const result = await integrationService.connectMintsoftIntegration({
				...credentials,
				...(id && { client_id: id }),
				...(id && clientName && { client_name: clientName })
			});
			await refetch();
			closeForm();
			if (result.verified === false) {
				toast.warning(`Mintsoft connected, but Kim could not verify it yet: ${result.warning ?? 'unknown error'}`, { duration: 15000 });
			} else {
				toast.success('Mintsoft connected');
			}
		} catch (err) {
			toast.error(errorMessage(err, 'Failed to connect Mintsoft. Please check the details and try again.'));
		} finally {
			setIsConnecting(false);
		}
	};

	const handleDisconnect = async () => {
		if (disconnectMutation.isPending) return;
		if (!(await confirmDisconnect(integration.name))) return;
		disconnectMutation.mutate(integration.id);
	};

	if (integration.isConnected && !isEditing) {
		const meta = integration.authMetaData ?? {};
		const isCredentials = meta.auth_type === 'credentials';
		const verificationError = meta.verified === false ? String(meta.verification_error ?? 'Kim could not verify these details.') : '';
		return (
			<div className='flex flex-col gap-4 px-5'>
				<div className='flex items-start justify-between'>
					<div className='flex flex-col gap-0.5'>
						<h3 className='text-sm'>Connected {integration.name} Account</h3>
						<p className='text-[10px] italic text-muted-foreground'>Manage or relink your {integration.name} connection.</p>
					</div>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant='ghost' size='icon-sm' aria-label='Mintsoft connection actions'>
								<MoreHorizontal className='size-4' />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align='end'>
							<DropdownMenuItem onClick={startEditing}>Update details</DropdownMenuItem>
							<DropdownMenuItem variant='destructive' onClick={handleDisconnect} disabled={disconnectMutation.isPending}>
								{disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>

				{verificationError && (
					<div role='alert' className='flex gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900'>
						<AlertTriangle className='size-4 shrink-0' />
						<div className='flex flex-col gap-1'>
							<span className='font-medium'>Not verified yet</span>
							<span>{verificationError}</span>
							<button type='button' className='self-start underline' onClick={startEditing}>
								Update details
							</button>
						</div>
					</div>
				)}

				<div className='flex flex-col gap-2 rounded-lg bg-muted p-3'>
					<Label className='text-xs text-muted-foreground'>Sign-in method</Label>
					<span className='text-sm font-medium'>{isCredentials ? 'Username & password (24-hour key, renewed automatically)' : 'Static API key'}</span>
				</div>
				{isCredentials && typeof meta.username === 'string' && (
					<div className='flex flex-col gap-2 rounded-lg bg-muted p-3'>
						<Label className='text-xs text-muted-foreground'>Mintsoft user</Label>
						<span className='text-sm font-medium'>{meta.username}</span>
					</div>
				)}
				<div className='flex flex-col gap-2 rounded-lg bg-muted p-3'>
					<Label className='text-xs text-muted-foreground'>Client</Label>
					<span className='text-sm font-medium'>{describeClient(meta)}</span>
				</div>
			</div>
		);
	}

	const clients = clientList?.clients ?? [];
	const warnUnscoped = Boolean(clientList?.canListClients && clients.length > 1 && !clientId.trim());

	return (
		<div className='flex h-full flex-col'>
			<div className='flex flex-1 flex-col gap-4 px-5'>
				<div className='flex flex-col gap-1'>
					<h3 className='text-sm font-semibold'>Link a Mintsoft Account</h3>
					<p className='text-sm text-muted-foreground'>
						Kim reads order status, despatch and tracking from Mintsoft to answer where-is-my-order questions. Connect with a static API key or
						with the username and password of a Mintsoft user.
					</p>
				</div>

				<fieldset className='flex flex-col gap-2'>
					<legend className='mb-2 text-sm font-medium'>Sign-in method</legend>
					{(
						[
							['api_key', 'Static API key', 'A key set to "Never Expire" by a Mintsoft administrator.'],
							['credentials', 'Username & password', 'Kim fetches a 24-hour key and renews it itself.']
						] as const
					).map(([value, label, hint]) => (
						<label key={value} className='flex cursor-pointer items-start gap-2 rounded-md border p-2'>
							<input
								type='radio'
								name='mintsoft-auth-type'
								value={value}
								checked={authType === value}
								onChange={() => {
									setAuthType(value);
									setClientList(null);
								}}
								className='mt-1'
							/>
							<span className='flex flex-col'>
								<span className='text-sm'>{label}</span>
								<span className='text-xs text-muted-foreground'>{hint}</span>
							</span>
						</label>
					))}
				</fieldset>

				{authType === 'api_key' ? (
					<div className='flex flex-col gap-2'>
						<Label htmlFor='mintsoft-api-key'>API Key</Label>
						<Input
							id='mintsoft-api-key'
							type='password'
							autoComplete='off'
							maxLength={MAX_FIELD_LENGTH}
							placeholder='Enter your Mintsoft API key'
							value={apiKey}
							onChange={onCredentialChange(setApiKey)}
						/>
					</div>
				) : (
					<>
						<div className='flex flex-col gap-2'>
							<Label htmlFor='mintsoft-username'>Mintsoft username</Label>
							<Input
								id='mintsoft-username'
								autoComplete='off'
								maxLength={MAX_FIELD_LENGTH}
								placeholder='The Mintsoft user Kim signs in as'
								value={username}
								onChange={onCredentialChange(setUsername)}
							/>
						</div>
						<div className='flex flex-col gap-2'>
							<Label htmlFor='mintsoft-password'>Mintsoft password</Label>
							<Input
								id='mintsoft-password'
								type='password'
								autoComplete='new-password'
								maxLength={MAX_FIELD_LENGTH}
								placeholder='Not your Access Evo password'
								value={password}
								onChange={onCredentialChange(setPassword)}
							/>
						</div>
					</>
				)}

				<div className='flex flex-col gap-2'>
					<Label htmlFor='mintsoft-client'>Client (for 3PL warehouse users)</Label>
					<div className='flex gap-2'>
						{clients.length > 0 ? (
							<select
								id='mintsoft-client'
								className='h-9 w-full rounded-md border bg-transparent px-3 text-sm'
								value={clientId}
								onChange={(e) => selectClient(e.target.value, clients.find((c) => c.id === e.target.value)?.name)}
							>
								<option value=''>No client filter</option>
								{clients.map((c) => (
									<option key={c.id} value={c.id}>
										{c.name}
										{c.code ? ` (${c.code})` : ''}
									</option>
								))}
							</select>
						) : (
							<Input
								id='mintsoft-client'
								inputMode='numeric'
								maxLength={20}
								placeholder='Client ID (optional)'
								value={clientId}
								onChange={(e) => selectClient(e.target.value.replace(/\D/g, ''))}
							/>
						)}
						<Button type='button' variant='outline' onClick={handleLoadClients} disabled={isLoadingClients || !credentials}>
							{isLoadingClients ? 'Loading…' : 'Load clients'}
						</Button>
					</div>
					{clientList && !clientList.canListClients && (
						<p className='text-xs text-muted-foreground'>This is a client user, so Mintsoft already limits it to its own client. Leave this blank.</p>
					)}
					{clientList?.canListClients && clients.length === 0 && <p className='text-xs text-muted-foreground'>This account has no clients.</p>}
					{warnUnscoped && (
						<p className='text-xs text-amber-700'>
							This user can see every client of the 3PL. Pick the brand&apos;s client so Kim only reads its orders.
						</p>
					)}
				</div>

				<SetupGuide authType={authType} />
			</div>

			<DialogFooter className='px-5 pb-5 pt-4'>
				{integration.isConnected && (
					<Button type='button' variant='outline' size={'lg'} onClick={closeForm}>
						Cancel
					</Button>
				)}
				<Button type='button' className='flex-1' size={'lg'} onClick={handleConnect} disabled={isConnecting || !credentials || warnUnscoped}>
					<Radio className='size-4' />
					{isConnecting ? 'Connecting…' : 'Connect Mintsoft'}
				</Button>
			</DialogFooter>
		</div>
	);
};
